import React, { useEffect, useMemo, useState } from "react";
import {
  styled,
  Button as MButton,
  Rating,
  Typography,
  Button,
} from "@mui/material";
import {
  ThumbDown,
  ThumbDownOffAlt,
  ThumbUp,
  ThumbUpOffAlt,
} from "@mui/icons-material";
import PropTypes from "prop-types";

function NCXRating(props) {
  const { text, validation, subType, sendMessageToPreview, id, disabled } =
    props;
  const [userRatingThumbEmoji, setUserRatingThumbEmoji] = useState(
    disabled ? 6 : -1
  );
  const [userRatingNumberStar, setUserRatingNumberStar] = useState(
    disabled ? 11 : 0
  );

  function getSafeValidationMax() {
    const { maxLimit } = validation;
    return maxLimit && maxLimit >= 2 ? maxLimit : 5;
  }

  function customNumberIcons() {
    const { maxLimit } = validation;
    let numberIconObj = [];
    for (let rateNum = 1; rateNum <= maxLimit; rateNum++) {
      numberIconObj.push({
        label: rateNum,
        icon: <span style={{ textAlign: "center" }}>{rateNum}</span>,
      });
    }
    return numberIconObj;
  }

  const emojiIcons = [
    {
      icon: <span style={{ fontSize: "20px" }}>&#128547;</span>,
      label: 0,
    },
    {
      icon: <span style={{ fontSize: "20px" }}>&#128577;</span>,
      label: 1,
    },
    {
      icon: <span style={{ fontSize: "20px" }}>&#128528;</span>,
      label: 2,
    },
    {
      icon: <span style={{ fontSize: "20px" }}>&#128578;</span>,
      label: 3,
    },
    {
      icon: <span style={{ fontSize: "20px" }}>&#128516;</span>,
      label: 4,
    },
  ];

  const thumbClickType = Object.freeze({
    UP: "up",
    DOWN: "down",
  });

  function thumbRatingClicked(type) {
    setUserRatingThumbEmoji((prevRating) =>
      type === thumbClickType.UP
        ? prevRating === 1
          ? -1
          : 1
        : prevRating === 0
        ? -1
        : 0
    );
  }

  const StyledRating = styled(Rating)(({ theme }) => ({
    "& .MuiRating-iconEmpty .MuiSvgIcon-root": {
      color: "gray",
    },
  }));

  function showRatingBySubType() {
    switch (subType) {
      case "Number":
        return (
          <div
            className="ratingHorizontalContainer"
            key={`${id}-ratinghorizontalcontainernumber`}
          >
            {customNumberIcons().map((numbIcon) => (
              <button
                disabled={userRatingNumberStar > 0}
                onClick={(_) => setUserRatingNumberStar(numbIcon.label)}
                className="ratingNumbButton"
                key={`${id}-ratingnumbbutton-${numbIcon.label}`}
              >
                {numbIcon.icon}
              </button>
            ))}
          </div>
        );
      case "Emoji":
        return (
          <div
            className="ratingHorizontalContainer"
            key={`${id}-ratinghorizontalcontaineremoji`}
          >
            {emojiIcons.map((emoji) => (
              <button
                disabled={userRatingThumbEmoji > -1}
                onClick={(_) => setUserRatingThumbEmoji(emoji.label)}
                className="ratingEmojiButton"
                key={`${id}-ratingemojibutton-${emoji.label}`}
              >
                {emoji.icon}
              </button>
            ))}
          </div>
        );
      case "Thumbs":
        return (
          <div
            className="ratingHorizontalContainer"
            key={`${id}-ratinghorizontalcontainerthumbs`}
          >
            <MButton
              onClick={() => thumbRatingClicked(thumbClickType.DOWN)}
              className="ratingThumbButton ratingThumbDown"
              startIcon={
                userRatingThumbEmoji === 0 ? (
                  <ThumbDown color="error" />
                ) : userRatingThumbEmoji === 1 ? (
                  <ThumbDownOffAlt color="error" />
                ) : (
                  <ThumbDown sx={{ color: "white" }} />
                )
              }
              disabled={userRatingThumbEmoji > -1}
              key={`${id}-ratingthumbbuttondown`}
            />
            <MButton
              onClick={() => thumbRatingClicked(thumbClickType.UP)}
              className="ratingThumbButton ratingThumbUp"
              endIcon={
                userRatingThumbEmoji === 1 ? (
                  <ThumbUp color="success" />
                ) : userRatingThumbEmoji === 0 ? (
                  <ThumbUpOffAlt color="success" />
                ) : (
                  <ThumbUp sx={{ color: "white" }} />
                )
              }
              disabled={userRatingThumbEmoji > -1}
              key={`${id}-ratingthumbbuttonup`}
            />
          </div>
        );
      case "Star":
      default:
        return (
          <Rating
            max={getSafeValidationMax()}
            value={userRatingNumberStar}
            onChange={(e, v) => setUserRatingNumberStar(v)}
            disabled={userRatingNumberStar > 0}
            sx={{
              fontSize: "2em",
            }}
            key={`${id}-ratingratingcarddefault`}
          />
        );
    }
  }

  const selectRatingPreviewMessage = () => {
    const { minLimit, maxLimit } = validation;

    if (subType === "Emoji") {
      switch (userRatingThumbEmoji) {
        case 0:
          return "Highly Displeased";
        case 1:
          return "Displeased";
        case 2:
          return "Indifferent";
        case 3:
          return "Happy";
        case 4:
          return "Extremely Happy";
        default:
          return "Indifferent";
      }
    }

    if (subType === "Number") {
      return userRatingNumberStar.toString();
    }

    if (subType === "Thumbs") {
      switch (userRatingThumbEmoji) {
        case 0:
          return "Disliked";
        case 1:
          return "Liked";
      }
    }

    if (subType === "Star") {
      return `${userRatingNumberStar} Stars`;
    }
  };

  useEffect(() => {
    if (userRatingThumbEmoji < 6 && userRatingThumbEmoji > -1) {
      sendMessageToPreview(
        (userRatingThumbEmoji + 1).toString(),
        props.componentId,
        selectRatingPreviewMessage()
      );
    }
  }, [userRatingThumbEmoji]);

  useEffect(() => {
    if (userRatingNumberStar < 11 && userRatingNumberStar > 0) {
      sendMessageToPreview(
        userRatingNumberStar.toString(),
        props.componentId,
        selectRatingPreviewMessage()
      );
    }
  }, [userRatingNumberStar]);

  return (
    <div className="ratingContainer" key={`${id}-ratingcontainer`}>
      <Typography
        variant="h5"
        style={{
          width: "100%",
          marginLeft: "8px",
          textAlign: "left",
          color: "black",
        }}
        key={`${id}-ratingtitle`}
      >
        {text}
      </Typography>
      {showRatingBySubType()}
    </div>
  );
}

export default NCXRating;
